import {
  AppBar,
  Box,
  IconButton,
  Skeleton,
  SvgIcon,
  Toolbar,
  List,
  ListItem,
  Link,
  Collapse,
  Typography,
} from "@mui/material";
import {
  Link as RouterLink,
  useLocation,
  useMatch,
  useNavigate,
} from "react-router-dom";
import { ReactComponent as JumpLogo } from "~@/assets/icons/logo.svg";
import CitySelector from "~@/components/layout/CitySelector";
import TournamentSelector from "~@/components/layout/TournamentSelector";
import { useCallback, useEffect, useMemo, useState } from "react";
import useCitiesQuery from "~@/api/queries/useCitiesQuery";
import useAcademyCitiesQuery from "~@/api/queries/useAcademyCitiesQuery";
import useAcademyVenuesQuery from "~@/api/queries/useAcademyVenuesQuery";
import compact from "lodash/compact";
import first from "lodash/first";
import isEmpty from "lodash/isEmpty";
import useTournamentsByCityQuery from "~@/api/queries/useTournamentsByCityQuery";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import MenuOutlined from "@mui/icons-material/MenuOutlined";
import map from "lodash/map";
import { useHomeContext } from "~@/pages/home-page/context/home-context";
import { useAnalytics } from "~@/analytics/useAnalytics";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";

export const menuItems = [
  { id: "/section-academy", label: "Секция J-Academy" },
  { id: "/schedule", label: "Расписание" },
  { id: "/tournament-table", label: "Турнирная таблица и плей-офф" },
  { id: "/statistics", label: "Статистика" },
  { id: "/leaderboard", label: "Лидерборд" },
  { id: "/teams", label: "Команды" },
  { id: "/players", label: "Игроки" },
];

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);

  const match1 = useMatch("/players");
  const match2 = useMatch("/teams");
  const match3 = useMatch("/login");
  const match4 = useMatch("/profile");
  const match5 = useMatch("/section-academy");
  const personalMatchBase = useMatch("/profile/personal");
  const personalMatchNested = useMatch("/profile/personal/*");
  const isPersonalPage = Boolean(personalMatchBase || personalMatchNested);

  const navigate = useNavigate();

  const location = useLocation();

  const {
    cityId,
    setCityId,
    academyCityId,
    setAcademyCityId,
    academyVenueId,
    setAcademyVenueId,
    tournamentId,
    setTournamentId,
  } = useHomeContext();
  const { trackEvent, events } = useAnalytics();

  const { data: citiesData, isLoading: areCitiesLoading } = useCitiesQuery();
  // J-Academy page takes cities from the J-Academy backend
  const { data: academyCitiesData, isLoading: areAcademyCitiesLoading } =
    useAcademyCitiesQuery(Boolean(match5));
  const { data: academyVenuesData, isLoading: areAcademyVenuesLoading } =
    useAcademyVenuesQuery(Boolean(match5));

  const {
    data: tournamentsData,
    isLoading: areTournamentsLoading,
    isPending: areTournamentsPending,
  } = useTournamentsByCityQuery(cityId);

  const cities = useMemo(() => {
    const allCities = compact(citiesData);
    const isHomePage = location.pathname === "/";

    if (isHomePage) {
      return allCities.filter((city) => {
        const cityTitle = city.title?.toLowerCase().trim();
        return cityTitle === "алматы" || cityTitle === "астана";
      });
    }

    return allCities;
  }, [citiesData, location.pathname]);

  const academyCities = useMemo(
    () => compact(academyCitiesData),
    [academyCitiesData],
  );

  // /venues ignores ?cityId, so filter by city here.
  // Venues without a city (cityId: null) are shown in every city.
  const academyVenues = useMemo(
    () =>
      compact(academyVenuesData).filter(
        (venue) => venue.cityId === null || venue.cityId === academyCityId,
      ),
    [academyVenuesData, academyCityId],
  );

  const tournaments = useMemo(() => {
    return compact(tournamentsData);
  }, [tournamentsData]);

  const handleMenuToggle = useCallback(() => {
    setMenuOpen((prev) => {
      const next = !prev;
      trackEvent(events.MENU_TOGGLE, { open: next });
      return next;
    });
  }, [events.MENU_TOGGLE, trackEvent]);

  const onCityChange = useCallback(
    (value: number) => {
      setCityId(value);
      setTournamentId(undefined);
      trackEvent(events.CITY_CHANGED, { city_id: value });
    },
    [events.CITY_CHANGED, setCityId, setTournamentId, trackEvent],
  );

  const onAcademyCityChange = useCallback(
    (value: number) => {
      setAcademyCityId(value);
      setAcademyVenueId(undefined);
      trackEvent(events.CITY_CHANGED, { city_id: value });
    },
    [events.CITY_CHANGED, setAcademyCityId, setAcademyVenueId, trackEvent],
  );

  const onTournamentChange = useCallback(
    (value: number) => {
      setTournamentId(value);
      trackEvent(events.TOURNAMENT_CHANGED, { tournament_id: value });
    },
    [events.TOURNAMENT_CHANGED, setTournamentId, trackEvent],
  );

  const setDefaultCity = useCallback(() => {
    if (isEmpty(cities)) return;

    const defaultId = first(cities)!.id;

    if (typeof defaultId === "number") {
      setCityId(defaultId);
    }
  }, [cities, setCityId]);

  const setDefaultTournament = useCallback(() => {
    if (isEmpty(tournaments) || isPersonalPage) return;

    const defaultId = first(tournaments)!.id;
    if (typeof defaultId === "number") {
      setTournamentId(defaultId);
    }
  }, [tournaments, setTournamentId, isPersonalPage]);

  useEffect(() => {
    if (typeof cityId === "undefined") {
      setDefaultCity();
    }
  }, [setDefaultCity, cityId]);

  useEffect(() => {
    if (typeof academyCityId !== "undefined" || isEmpty(academyCities)) return;

    const defaultId = first(academyCities)!.id;
    if (typeof defaultId === "number") {
      setAcademyCityId(defaultId);
    }
  }, [academyCities, academyCityId, setAcademyCityId]);

  useEffect(() => {
    if (typeof academyVenueId !== "undefined" || isEmpty(academyVenues)) return;

    setAcademyVenueId(first(academyVenues)!.id);
  }, [academyVenues, academyVenueId, setAcademyVenueId]);

  useEffect(() => {
    if (typeof tournamentId === "undefined") {
      setDefaultTournament();
    }
  }, [setDefaultTournament, tournamentId, cityId, tournaments]);

  useEffect(() => {
    const isHomePage = location.pathname === "/";

    if (isHomePage && typeof cityId !== "undefined" && !isEmpty(cities)) {
      const isCityAvailable = cities.some((city) => city.id === cityId);

      if (!isCityAvailable) {
        const defaultCityId = first(cities)?.id;
        if (typeof defaultCityId === "number") {
          setCityId(defaultCityId);
          setTournamentId(undefined);
        }
      }
    }
  }, [location.pathname, cityId, cities, setCityId, setTournamentId]);

  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  return (
    <AppBar
      position="sticky"
      color="transparent"
      elevation={0}
      sx={{
        borderBottom: "1px solid #e5e5e5",
        bgcolor: "background.paper",
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          flexDirection: "column",
          px: 2,
          pt: 3,
          pb: menuOpen ? 2 : !match1 && !match2 ? 1.5 : 2,
          gap: 2,
          height: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
          }}
        >
          <RouterLink to="/" style={{ display: "contents" }}>
            <SvgIcon
              sx={{
                width: "auto",
                height: 32,
              }}
              component={JumpLogo}
              inheritViewBox
            />
          </RouterLink>
          <Box flexGrow={1} />
          {match3 || match4 ? null : match5 ? (
            areAcademyCitiesLoading ||
            typeof academyCityId === "undefined" ? (
              <Skeleton variant="rounded" width={91} height={32} />
            ) : (
              <Box sx={{ width: 91 }}>
                <CitySelector
                  cities={academyCities}
                  cityId={academyCityId}
                  onCityChange={onAcademyCityChange}
                />
              </Box>
            )
          ) : areCitiesLoading || typeof cityId === "undefined" ? (
            <Skeleton variant="rounded" width={91} height={32} />
          ) : (
            <Box sx={{ width: 91 }}>
              <CitySelector
                cities={cities}
                cityId={cityId}
                onCityChange={onCityChange}
              />
            </Box>
          )}
          <IconButton
            component={RouterLink}
            to="/profile"
            aria-label="profile"
            onClick={() =>
              trackEvent(events.NAVIGATION_CLICK, {
                target: "/profile",
                source: "header_profile",
              })
            }
            sx={{
              ml: 3,
              p: 0,
              width: 28,
              height: 28,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <SvgIcon sx={{ fontSize: 24, color: "#000" }} viewBox="0 0 24 24">
              <defs>
                <clipPath id="profClip">
                  <circle cx="12" cy="12" r="9" />
                </clipPath>
              </defs>
              <circle
                cx="12"
                cy="12"
                r="10"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              />
              <g clipPath="url(#profClip)" fill="currentColor">
                <circle cx="12" cy="10" r="4" />
                <path d="M5 18 C8 15 16 15 19 18 L19 24 L5 24 Z" />
              </g>
            </SvgIcon>
          </IconButton>

          <IconButton
            aria-label={menuOpen ? "close menu" : "open menu"}
            onClick={handleMenuToggle}
            sx={{
              ml: 3,
              p: 0,
              width: 32,
              height: 32,
              "& .MuiSvgIcon-root": {
                fontSize: 24,
              },
            }}
          >
            {menuOpen ? <CloseOutlined /> : <MenuOutlined />}
          </IconButton>
        </Box>

        <Collapse
          in={menuOpen}
          timeout={300}
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            flexGrow: 1,
            mt: -1,
            mb: -1,
          }}
          mountOnEnter
          unmountOnExit
        >
          <List sx={{ width: "100%", py: 1 }}>
            {map(menuItems, (item, index) => (
              <ListItem
                key={item.id}
                sx={{
                  px: 0,
                  py: 1,
                  transform: "translateX(-8px)",
                  opacity: 0,
                  animation: "slideIn 0.3s forwards",
                  animationDelay: `${index * 0.05}s`,
                  "@keyframes slideIn": {
                    "0%": {
                      transform: "translateX(-8px)",
                      opacity: 0,
                    },
                    "100%": {
                      transform: "translateX(0)",
                      opacity: 1,
                    },
                  },
                }}
              >
                <Link
                  component={RouterLink}
                  to={item.id}
                  onClick={() =>
                    trackEvent(events.NAVIGATION_CLICK, {
                      target: item.id,
                      source: "header_menu",
                    })
                  }
                  underline="none"
                  sx={{
                    fontWeight: 400,
                    fontSize: 16,
                    lineHeight: 1.3,
                    color: "text.primary",
                  }}
                >
                  {item.label}
                </Link>
              </ListItem>
            ))}
          </List>
        </Collapse>

        {match5 && (areAcademyVenuesLoading || !isEmpty(academyVenues)) && (
          <Collapse
            in={!menuOpen}
            timeout={300}
            sx={{ display: "flex", alignItems: "center", width: "100%" }}
            mountOnEnter
            unmountOnExit
          >
            {areAcademyVenuesLoading ||
            typeof academyVenueId === "undefined" ? (
              <Skeleton variant="rounded" width={"100%"} height={32} />
            ) : (
              <TournamentSelector
                tournaments={academyVenues}
                tournamentId={academyVenueId}
                onTournamentChange={setAcademyVenueId}
                backgroundColor="gray"
              />
            )}
          </Collapse>
        )}

        {!match1 && !match2 && !match3 && !match4 && !match5 && (
          <Collapse
            in={!menuOpen}
            timeout={300}
            sx={{ display: "flex", alignItems: "center", width: "100%" }}
            mountOnEnter
            unmountOnExit
          >
            {isPersonalPage ? (
              <Box
                component="button"
                type="button"
                onClick={() => navigate(-1)}
                style={{ all: "unset", cursor: "pointer" }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <ChevronLeftIcon
                    sx={{ fontSize: 20, color: "text.primary" }}
                  />
                  <Typography
                    sx={{
                      fontSize: 16,
                      fontWeight: 600,
                      color: "text.primary",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Личные данные
                  </Typography>
                </Box>
              </Box>
            ) : areTournamentsPending ||
              areTournamentsLoading ||
              typeof tournamentId === "undefined" ? (
              <Skeleton variant="rounded" width={"100%"} height={32} />
            ) : (
              <TournamentSelector
                tournaments={tournaments}
                tournamentId={tournamentId}
                onTournamentChange={onTournamentChange}
                backgroundColor="gray"
              />
            )}
          </Collapse>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Header;
